import styled, { css, keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

const enterFromRight = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to { opacity: 1; transform: none; }
`;

const enterFromAbove = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: none; }
`;

/**
 * Where the toasts stack: the top right; on a phone, the top (the bottom has its nav and the screens' save buttons
 * right above it, which a toast there would cover), the newest at the top.
 */
export const Viewport = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: min(420px, calc(100vw - 32px));
  pointer-events: none;

  @media ${MOBILE} {
    top: calc(16px + env(safe-area-inset-top, 0px));
    right: 16px;
    flex-direction: column-reverse;
  }
`;

const tones = {
  success: { ink: "#0d3d1c", fill: "var(--color-brand-plus)" },
  error: { ink: "#fff", fill: "var(--color-feedback-danger)" },
  info: { ink: "#fff", fill: "var(--color-brand-blue)" },
};

export const Item = styled.div<{ $kind: keyof typeof tones; $leaving: boolean }>`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 12px 12px 12px 14px;
  font-family: Inter, sans-serif;
  pointer-events: auto;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
  animation: ${enterFromRight} 0.22s ease-out;
  transition:
    opacity 0.2s,
    transform 0.2s;

  ${({ $leaving }) =>
    $leaving &&
    css`
      opacity: 0;
      transform: scale(0.98);
    `}

  @media ${MOBILE} {
    animation-name: ${enterFromAbove};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  .icon {
    display: grid;
    flex-shrink: 0;
    place-content: center;
    width: 28px;
    height: 28px;
    font-size: 0.875rem;
    font-weight: 800;
    color: ${({ $kind }) => tones[$kind].ink};
    background: ${({ $kind }) => tones[$kind].fill};
    border-radius: 100%;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .title {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .description {
    overflow: hidden;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .action {
    flex-shrink: 0;
    height: 32px;
    padding: 0 12px;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 999px;
  }

  .close {
    display: grid;
    flex-shrink: 0;
    place-content: center;
    width: 24px;
    height: 24px;
    font-size: 1rem;
    line-height: 1;
    color: var(--color-neutral-150);
    border-radius: 100%;

    &:hover {
      color: var(--color-neutral-white);
      background: var(--color-neutral-base);
    }
  }
`;
